// istanbul ignore file
import { jsPDFOptions } from "jspdf";
import { DateRange, RiskTypes } from "../type/enums";
import { RadioOption } from "../interface";
import { TabIndex } from "../../src/interface";

/**
 * Column Names for CSV & PDF exports.
 */
export const requiredColumns = [
  { headerName: "Source ID", field: "source_system" },
  { headerName: "Text Sentinel ID", field: "ts_id" },
  { headerName: "Parent Record ID", field: "parent_record_id" },
  { headerName: "Child Record ID", field: "child_record_id" },
  { headerName: "Therapeutic Area", field: "therapeutic_area" },
  { headerName: "Functionality Title", field: "functionality_title" },
  { headerName: "Risk", field: "qc_queue_sampling" },
  { headerName: "Updated Record", field: "updated_record" },
  { headerName: "Evaluation (Pass/Fail)", field: "evaluation_risk_type" },
  { headerName: "HC Risk", field: "hc_severity" },
  { headerName: "AE, PC, SS Risk", field: "ae_pc_ss_severity" },
  { headerName: "AE/SS Number", field: "adverse_event_number" },
  { headerName: "Product Complaint Number", field: "product_complaint_number" },
  { headerName: "Review Type", field: "review_type" },
  { headerName: "Status", field: "status" },
  { headerName: "Country", field: "country" },
  { headerName: "Product", field: "product" },
  { headerName: "Indication", field: "indication" },
  { headerName: "OCE-P Link", field: "ocep_link" },
  { headerName: "OCE-P User Email ID", field: "ocep_user_email_id" },
  { headerName: "OCE-P User Job Title", field: "ocep_user_job_title" },
  { headerName: "Responsible Email ID", field: "responsible_email" },
  { headerName: "Responsible Job Title", field: "responsible_job_title" },
  { headerName: "OCE-P Owner Email ID", field: "ocep_owner_email_id" },
  {
    headerName: "Text Sentinel Extraction Date",
    field: "ftqc_extraction_date",
  },
  { headerName: "QC Queue Sampling", field: "qc_queue_sampling" },
  { headerName: "QC Status", field: "qc_status" },
  { headerName: "User Name (OCE P)", field: "user_name_ocep" },
  { headerName: "Field Name", field: "field_name" },
  { headerName: "Field Value", field: "field_value" },
  { headerName: "Version number", field: "version_id" },
  { headerName: "Interaction Date", field: "interaction_date" },
  { headerName: "Creator Full Name", field: "creator_full_name" },
  { headerName: "Assigned", field: "assigned" },
  { headerName: "Assigned To", field: "assigned_to" },
  { headerName: "Assigned By", field: "assigned_by" },
  { headerName: "Assigned Date", field: "assigned_date" },
  { headerName: "Record Sampled Date", field: "record_sampled_date" },
  { headerName: "Corrective Action", field: "corrective_action" },
  { headerName: "Reviewed Date", field: "reviewed_date" },
];

/**
 * Column Names for CSV & PDF exports.
 */

export const requiredAuditLogColumns = [
  { headerName: "Audit Record Id", field: "audit_id" },
  { headerName: "Record ID", field: "ts_id" },
  { headerName: "User Id", field: "user_id" },
  { headerName: "Email Id", field: "user_email_id" },
  { headerName: "User Role", field: "user_role" },
  { headerName: "Extracted Date", field: "date_time_of_entry" },
  { headerName: "Text Content (Original)", field: "text" },
  { headerName: "Created date", field: "created_dt" },
  { headerName: "Type of Action", field: "action" },
  { headerName: "Evaluation", field: "evaluation" },
  // { headerName: "Evaluation Id", field: "evaluation_id" },
  {
    headerName: "Corrective action details",
    field: "corrective_action_details",
  },
  { headerName: "Review Type", field: "review_type" },
  { headerName: "Notification Status", field: "notification_status" },
  { headerName: "Notification Timestamp", field: "notification_timestamp" },
  { headerName: "Notification Recipient(s)", field: "notification_recipient" },
  { headerName: "Country", field: "country" },
  { headerName: "Region", field: "region" },
  { headerName: "Sample ID", field: "sample_id" },
  { headerName: "Record Version", field: "version_id" },
  { headerName: "Evaluated Risk", field: "evaluated_risk" },
];

/**
 * Default options for jsPDF doc used in PDF exports.
 */
export const defaultJSPDFOptions: jsPDFOptions = {
  orientation: "landscape",
  unit: "pt",
  format: "A3",
};

/**
 * Default options for jsPDF autoTable used in PDF exports.
 */
export const defaultAutoTableOptions = {
  startY: 40,
  theme: "striped",
  tableWidth: "wrap",
  styles: {
    fontSize: 10,
    cellWidth: "wrap",
    overflow: "linebreak",
  },
  headStyles: {
    fillColor: [22, 160, 133],
    textColor: 255,
    lineWidth: 0.5,
  },
  bodyStyles: {
    lineWidth: 0.5,
  },
  margin: { top: 40, right: 20, left: 20 },
};

export const RiskType: Record<RiskTypes, string> = {
  [RiskTypes.PC]: "Product Compliant",
  [RiskTypes.SS]: "Special Situation",
  [RiskTypes.NR]: "No Risk",
  [RiskTypes.HC]: "Healthcare Compliance",
  [RiskTypes.AE]: "Adverse Event",
};

export const tabName: TabIndex = {
  0: "Needs Review",
  1: "Reviewed",
  2: "No Risk",
};

export const DateRangeOptions: RadioOption[] = [
  { value: DateRange.YESTERDAY, label: "Yesterday" },
  { value: DateRange.LAST_7_DAYS, label: "Last 7 Days" },
  { value: DateRange.LAST_14_DAYS, label: "Last 14 Days" },
  { value: DateRange.THIS_MONTH, label: "This Month" },
  { value: DateRange.PREVIOUS_MONTH, label: "Previous Month" },
  { value: DateRange.CUSTOM, label: "Custom Date Range" },
];

export const DateRangeLabels: Record<DateRange, string> =
  DateRangeOptions.reduce(
    (acc, { value, label }) => {
      acc[value as DateRange] = label;
      return acc;
    },
    {} as Record<DateRange, string>,
  );

export const ReviewTypeOptions: RadioOption[] = [
  { value: "PTQ", label: "PTQ" },
  { value: "PCO", label: "PCO" },
  { value: "PV", label: "PV" },
];

export const envMapping: Record<string, string> = {
  "text-sentinel-dev.aws.cloud.roche.com": "TextSentinel_env_DEV",
  "www.qa.textsentinel.roche.com": "TextSentinel_env_TEST",
  "www.textsentinel.roche.com": "TextSentinel_env_PROD",
  localhost: "TextSentinel_env_DEV",
};

export const roleMapping: Record<string, string> = {
  "text-sentinel-dev.aws.cloud.roche.com": "TextSentinel_profile_group_admin",
  "www.qa.textsentinel.roche.com": "TextSentinel_profile_group_admin",
  "www.textsentinel.roche.com": "TextSentinel_profile_group_admin",
  localhost: "TextSentinel_profile_group_admin",
};
